import type { SmileSimulationController } from "../hooks/useSmileSimulationController";
import { getSmileSimulationCopy } from "../model/i18n";
import type { SmileSimulationLanguage } from "../model/types";

type SmileSimulationPatientInfoPageProps = {
  clinic: SmileSimulationController["clinic"];
  draft: SmileSimulationController["patient"]["draft"];
  isExperience?: boolean;
  language: SmileSimulationLanguage;
  onChartNumberChange: SmileSimulationController["actions"]["updatePatientChartNumber"];
  onNameChange: SmileSimulationController["actions"]["updatePatientName"];
  onSubmit: SmileSimulationController["actions"]["submitPatientInfo"];
};

export function SmileSimulationPatientInfoPage({
  clinic,
  draft,
  isExperience = false,
  language,
  onChartNumberChange,
  onNameChange,
  onSubmit,
}: SmileSimulationPatientInfoPageProps) {
  const copy = getSmileSimulationCopy(language);

  return (
    <section className="w-full max-w-md">
      <div className="space-y-2 text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#8F14E5]/70">
          {isExperience ? "Experience Intake" : "Patient Intake"}
        </p>
        <h2 className="text-2xl font-semibold text-[#1f1230] sm:text-[1.75rem]">
          {isExperience
            ? "참가자 정보를 입력해 주세요"
            : copy.patientInfo.title}
        </h2>
        {!isExperience ? (
          <p className="text-sm leading-6 text-[#5f3a78]">
            {copy.patientInfo.accountLabel}{" "}
            <span className="font-semibold text-[#1f1230]">{clinic.email}</span>
          </p>
        ) : null}
      </div>

      <div className="mt-6 rounded-[1.75rem] border border-white/70 bg-white/90 p-6 shadow-[0_20px_50px_rgba(17,17,17,0.08)] backdrop-blur-sm">
        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <div>
            <label
              htmlFor="smile-simulation-patient-name"
              className="block text-sm font-medium text-gray-900"
            >
              {isExperience ? "참가자명" : copy.patientInfo.nameLabel}
            </label>
            <div className="mt-2">
              <input
                id="smile-simulation-patient-name"
                type="text"
                value={draft.name}
                onChange={(event) => onNameChange(event.target.value)}
                placeholder={copy.patientInfo.namePlaceholder}
                aria-invalid={Boolean(draft.nameError)}
                aria-describedby={
                  draft.nameError
                    ? "smile-simulation-patient-name-error"
                    : undefined
                }
                className="block h-11 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-[#8F14E5] focus:outline-none focus:ring-2 focus:ring-[#8F14E5] sm:text-sm"
              />
            </div>
            {draft.nameError ? (
              <p
                id="smile-simulation-patient-name-error"
                className="mt-2 text-sm font-medium text-red-600"
              >
                {draft.nameError}
              </p>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="smile-simulation-patient-chart"
              className="block text-sm font-medium text-gray-900"
            >
              {isExperience ? "참가 코드" : copy.patientInfo.chartLabel}
            </label>
            <div className="mt-2">
              <input
                id="smile-simulation-patient-chart"
                type="text"
                value={draft.chartNumber}
                onChange={(event) => onChartNumberChange(event.target.value)}
                placeholder={copy.patientInfo.chartPlaceholder}
                aria-invalid={Boolean(draft.chartNumberError)}
                aria-describedby={
                  draft.chartNumberError
                    ? "smile-simulation-patient-chart-error"
                    : undefined
                }
                className="block h-11 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-[#8F14E5] focus:outline-none focus:ring-2 focus:ring-[#8F14E5] sm:text-sm"
              />
            </div>
            {draft.chartNumberError ? (
              <p
                id="smile-simulation-patient-chart-error"
                className="mt-2 text-sm font-medium text-red-600"
              >
                {draft.chartNumberError}
              </p>
            ) : null}
          </div>

          <div className="pt-2">
            <button
              type="submit"
              className="h-12 w-full shrink-0 rounded-md bg-[#8F14E5] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#7d10ca]"
            >
              {copy.patientInfo.next}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
